<template>
  <v-container fluid class="layout-generator">
    <v-row>
      <!-- Toolbar -->
      <v-col cols="12">
        <v-toolbar color="primary" class="px-2">
          <v-toolbar-title>AHU Card Layout Builder</v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- Site ID Input -->
          <v-text-field
            v-model.number="layout.siteId"
            label="Site ID"
            type="number"
            variant="outlined"
            density="compact"
            hide-details
            class="mr-4"
            style="width: 120px"
          ></v-text-field>

          <v-btn
            variant="outlined"
            color="white"
            @click="deleteElement()"
            :disabled="!selectedElement"
            class="ml-2"
          >
            <v-icon>mdi-delete-outline</v-icon>
            Delete
          </v-btn>
          <v-btn variant="outlined" color="white" @click="clearCanvas">
            <v-icon>mdi-delete</v-icon>
            Clear
          </v-btn>
          <v-btn
            variant="outlined"
            color="white"
            @click="previewLayout"
            class="ml-2"
          >
            <v-icon>mdi-eye</v-icon>
            Contract Preview
          </v-btn>
          <v-btn
            variant="outlined"
            color="white"
            @click="saveLayout"
            class="ml-2"
          >
            <v-icon>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-toolbar>
      </v-col>
    </v-row>

    <v-row class="fill-height">
      <!-- Element Palette -->
      <v-col cols="3" class="palette-panel">
        <v-card class="h-100">
          <v-card-title class="text-subtitle-1">Elements</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div class="palette-section">
              <h4 class="text-body-2 font-weight-bold mb-2">Layout</h4>
              <v-chip
                v-for="elementType in containerElements"
                :key="elementType.type"
                class="ma-1"
                :draggable="true"
                @dragstart="onDragStart($event, elementType)"
                @dragend="onDragEnd"
                color="primary"
                variant="outlined"
              >
                <v-icon>{{ elementType.icon }}</v-icon>
                {{ elementType.label }}
              </v-chip>
            </div>

            <div class="palette-section mt-4">
              <h4 class="text-body-2 font-weight-bold mb-2">Inputs</h4>
              <v-chip
                v-for="inputType in inputElements"
                :key="inputType.type"
                class="ma-1"
                :draggable="true"
                @dragstart="onDragStart($event, inputType)"
                @dragend="onDragEnd"
                color="secondary"
                variant="outlined"
              >
                <v-icon>{{ inputType.icon }}</v-icon>
                {{ inputType.label }}
              </v-chip>
            </div>

            <div class="palette-section mt-4">
              <h4 class="text-body-2 font-weight-bold mb-2">Special</h4>
              <v-chip
                v-for="specialType in specialElements"
                :key="specialType.type"
                class="ma-1"
                :draggable="true"
                @dragstart="onDragStart($event, specialType)"
                @dragend="onDragEnd"
                color="accent"
                variant="outlined"
              >
                <v-icon>{{ specialType.icon }}</v-icon>
                {{ specialType.label }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Canvas Area -->
      <v-col cols="6" class="canvas-panel">
        <v-card class="h-100 d-flex flex-column">
          <v-card-title class="text-subtitle-1 flex-shrink-0">
            Canvas (12-Column Grid)
            <v-switch
              v-model="showGrid"
              label="Show Grid"
              density="compact"
              class="ml-4"
              hide-details
            ></v-switch>
          </v-card-title>
          <v-divider class="flex-shrink-0"></v-divider>
          <v-card-text class="pa-0 flex-grow-1 d-flex flex-column">
            <div
              ref="canvasRef"
              class="canvas-container flex-grow-1"
              :class="{ 'show-grid': showGrid, 'dragging-active': isDragging }"
              @drop="onDrop"
              @dragover="onDragOver"
              @dragenter.prevent
            >
              <!-- Grid Guidelines -->
              <div v-if="showGrid" class="grid-guidelines">
                <div
                  v-for="i in 12"
                  :key="i"
                  class="grid-line"
                  :style="{ left: `${(i - 1) * (100 / 12)}%` }"
                ></div>
              </div>

              <!-- Rendered Layout Elements -->
              <div class="layout-content container-fluid">
                <div class="row">
                  <template
                    v-for="(element, index) in layout.elements"
                    :key="element.id"
                  >
                    <!-- Drop zone indicator -->
                    <div
                      v-if="isDragging && dragOverIndex === index"
                      class="drop-zone-indicator col-12"
                    >
                      <div class="drop-line"></div>
                    </div>

                    <div
                      :class="[
                        element.class,
                        { 'dragging-spacing': isDragging },
                      ]"
                      :data-element-index="index"
                    >
                      <layout-element-component
                        :element="element"
                        :selected="selectedElement?.id === element.id"
                        :selected-element-id="selectedElement?.id"
                        @select="selectElement"
                        @delete="deleteElement"
                        @update="updateElement"
                        @group-drop="onGroupDrop"
                        @label-drop="onLabelDrop"
                      />
                    </div>
                  </template>

                  <!-- Final drop zone (after last element) -->
                  <div
                    v-if="
                      isDragging && dragOverIndex === layout.elements.length
                    "
                    class="drop-zone-indicator col-12"
                  >
                    <div class="drop-line"></div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Properties Panel -->
      <v-col cols="3" class="properties-panel">
        <v-card class="h-100">
          <v-card-title class="text-subtitle-1">Properties</v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="selectedElement">
            <element-properties
              :element="selectedElement"
              @update="updateElement"
            />
          </v-card-text>
          <v-card-text v-else class="text-center text-disabled">
            Select an element to edit properties
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Preview Dialog -->
    <v-dialog v-model="showPreview" max-width="1200">
      <v-card>
        <v-card-title>Contract Preview</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div class="preview-container">
            <!-- Here we would render the actual AHU card with the generated layout -->
            <pre>{{ JSON.stringify(layout, null, 2) }}</pre>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showPreview = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, onUnmounted } from 'vue';
import {
  AHULayoutContract,
  LayoutElement,
  ElementType,
  InputType,
  GroupElement,
  InputElement,
  LabelElement,
  BigLabelElement,
  RowBreakElement,
} from '@/Core.Service.Domain/Controls/AHUs/types/AHULayout';

// Component imports (we'll create these next)
import LayoutElementComponent from './LayoutElement.vue';
import ElementProperties from './ElementProperties.vue';

// Canvas and grid state
const canvasRef = ref<HTMLElement>();
const showGrid = ref(true);
const showPreview = ref(false);

// Drag and drop state
const isDragging = ref(false);
const dragOverIndex = ref<number>(-1);

// Layout state
const layout = reactive<AHULayoutContract>({
  elements: [],
});

// Selection state
const selectedElement = ref<LayoutElement | null>(null);

// Element type definitions for palette
interface ElementTypeDefinition {
  type: string;
  label: string;
  icon: string;
  elementType: ElementType;
  inputType?: InputType;
}

const containerElements: ElementTypeDefinition[] = [
  {
    type: 'group',
    label: 'Group',
    icon: 'mdi-folder',
    elementType: 'group',
  },
];

const inputElements: ElementTypeDefinition[] = [
  {
    type: 'text-input',
    label: 'Text Input',
    icon: 'mdi-form-textbox',
    elementType: 'input',
    inputType: 'text',
  },
  {
    type: 'number-input',
    label: 'Number Input',
    icon: 'mdi-numeric',
    elementType: 'input',
    inputType: 'number',
  },
  {
    type: 'date-input',
    label: 'Date Input',
    icon: 'mdi-calendar',
    elementType: 'input',
    inputType: 'date',
  },
];

const specialElements: ElementTypeDefinition[] = [
  {
    type: 'bigTextSmallLabel',
    label: 'Big Label',
    icon: 'mdi-format-title',
    elementType: 'bigTextSmallLabel',
  },
  {
    type: 'row-break',
    label: 'Row Break',
    icon: 'mdi-keyboard-return',
    elementType: 'row-break',
  },
];

// Drag and drop functionality
let draggedElementType: ElementTypeDefinition | null = null;

const onDragStart = (event: DragEvent, elementType: ElementTypeDefinition) => {
  draggedElementType = elementType;
  isDragging.value = true;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'copy';
  }
};

const onDragEnd = () => {
  isDragging.value = false;
  dragOverIndex.value = -1;
  draggedElementType = null;
};

// Calculate insertion index based on drop position
const calculateInsertionIndex = (event: DragEvent): number => {
  if (layout.elements.length === 0) return 0;

  const rect = canvasRef.value?.getBoundingClientRect();
  if (!rect) return layout.elements.length;

  const y = event.clientY - rect.top;

  // Get all element positions
  const elementRects: {
    index: number;
    top: number;
    bottom: number;
    height: number;
  }[] = [];

  // Query all layout elements in the DOM
  const elementContainers = canvasRef.value?.querySelectorAll(
    '[data-element-index]'
  );

  if (elementContainers) {
    elementContainers.forEach((container, index) => {
      const containerRect = container.getBoundingClientRect();
      const relativeTop = containerRect.top - rect.top;
      const relativeBottom = containerRect.bottom - rect.top;

      elementRects.push({
        index,
        top: relativeTop,
        bottom: relativeBottom,
        height: containerRect.height,
      });
    });
  }

  // Sort by vertical position
  elementRects.sort((a, b) => a.top - b.top);

  // Find the best insertion point
  for (let i = 0; i < elementRects.length; i++) {
    const current = elementRects[i];
    const midPoint = current.top + current.height / 2;

    if (y < midPoint) {
      return current.index;
    }
  }

  // If we're past all elements, insert at the end
  return layout.elements.length;
};

const onDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = false;
  dragOverIndex.value = -1;

  if (!draggedElementType) return;

  const insertionIndex = calculateInsertionIndex(event);

  // Provide sensible default sizes based on element type
  let defaultSize = 4; // Default to 4 columns (1/3 width)

  switch (draggedElementType.elementType) {
    case 'group':
      defaultSize = 6; // Groups take half width by default
      break;
    case 'input':
      defaultSize = 4; // Inputs take 1/3 width by default
      break;
    case 'bigTextSmallLabel':
      defaultSize = 12; // Big labels take full width
      break;
    case 'row-break':
      defaultSize = 12; // Row breaks always take full width
      break;
  }

  const gridClass = `col-lg-${defaultSize} col-sm-12`;

  createElement(draggedElementType, gridClass, insertionIndex);
  draggedElementType = null;
};

// Handle drag over for visual feedback
const onDragOver = (event: DragEvent) => {
  event.preventDefault();
  if (!isDragging.value) return;

  const insertionIndex = calculateInsertionIndex(event);
  dragOverIndex.value = insertionIndex;
};

// Handle drop on group elements
const onGroupDrop = (payload: { parentId: string; dropEvent: DragEvent }) => {
  payload.dropEvent.preventDefault();
  payload.dropEvent.stopPropagation();

  if (!draggedElementType) return;

  const { parentId } = payload;

  // Find the parent group element
  const parentElement = findElementById(parentId);
  if (!parentElement || parentElement.type !== 'group') return;

  // Create the new element with appropriate sizing for group content
  let defaultSize = 12; // Default to full width inside groups

  switch (draggedElementType.elementType) {
    case 'input':
      defaultSize = 6; // Inputs take half width inside groups
      break;
    case 'bigTextSmallLabel':
      defaultSize = 12; // Big labels take full width
      break;
    case 'row-break':
      defaultSize = 12; // Row breaks always take full width
      break;
    case 'group':
      defaultSize = 12; // Nested groups take full width
      break;
  }

  const gridClass = `col-lg-${defaultSize} col-sm-12`;

  // Create the element and add it to the group's children
  const newElement = createElementObject(draggedElementType, gridClass);
  if (newElement) {
    (parentElement as GroupElement).children.push(newElement);
    selectedElement.value = newElement;
  }

  draggedElementType = null;
};

// Handle drop on big label elements (for adding child labels)
const onLabelDrop = (payload: { parentId: string; dropEvent: DragEvent }) => {
  payload.dropEvent.preventDefault();
  payload.dropEvent.stopPropagation();

  if (!draggedElementType) return;

  const { parentId } = payload;

  // Find the parent big label element
  const parentElement = findElementById(parentId);
  if (!parentElement || parentElement.type !== 'bigTextSmallLabel') return;

  draggedElementType = null;
};

// Helper functions
const findElementById = (id: string): LayoutElement | null => {
  // Search in main elements
  for (const element of layout.elements) {
    if (element.id === id) return element;

    // Search recursively in group children
    if (element.type === 'group') {
      const found = findElementInGroup(element as GroupElement, id);
      if (found) return found;
    }
  }
  return null;
};

const findElementInGroup = (
  group: GroupElement,
  id: string
): LayoutElement | null => {
  for (const child of group.children) {
    if (child.id === id) return child;

    if (child.type === 'group') {
      const found = findElementInGroup(child as GroupElement, id);
      if (found) return found;
    }
  }
  return null;
};

const createElementObject = (
  elementDef: ElementTypeDefinition,
  gridClass: string
): LayoutElement | null => {
  const id = `element_${Date.now()}_${Math.random()
    .toString(36)
    .substring(2, 9)}`;

  switch (elementDef.elementType) {
    case 'group':
      return {
        id,
        type: 'group',
        label: 'New Group',
        canonicalName: generateCanonicalName('New Group'),
        class: gridClass,
        children: [],
      } as GroupElement;

    case 'input':
      return {
        id,
        type: 'input',
        label: 'New Input',
        canonicalName: generateCanonicalName('New Input'),
        class: gridClass,
        inputType: elementDef.inputType || 'text',
      } as InputElement;

    case 'bigTextSmallLabel':
      return {
        id,
        type: 'bigTextSmallLabel',
        label: 'New Label',
        canonicalName: generateCanonicalName('New Label'),
        class: gridClass,
      } as BigLabelElement;

    case 'row-break':
      return {
        id,
        type: 'row-break',
      } as RowBreakElement;

    default:
      return null;
  }
};

// Element creation and management
const createElement = (
  elementDef: ElementTypeDefinition,
  gridClass: string,
  insertionIndex?: number
) => {
  const element = createElementObject(elementDef, gridClass);
  if (element) {
    if (
      insertionIndex !== undefined &&
      insertionIndex >= 0 &&
      insertionIndex < layout.elements.length
    ) {
      // Insert at specific index
      layout.elements.splice(insertionIndex, 0, element);
    } else {
      // Default behavior: append to end
      layout.elements.push(element);
    }
    selectedElement.value = element;
  }
};

// Utility functions
const generateCanonicalName = (label: string): string => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/^([0-9])/, '_$1'); // Ensure it doesn't start with a number
};

// Element management
const selectElement = (element: LayoutElement) => {
  selectedElement.value = element;
};

const deleteElement = (elementId?: string) => {
  // If no elementId provided, delete the currently selected element
  const targetId = elementId || selectedElement.value?.id;
  if (!targetId) {
    console.warn('No element selected for deletion');
    return;
  }

  // First try to delete from main elements
  const index = layout.elements.findIndex((el) => el.id === targetId);
  if (index !== -1) {
    layout.elements.splice(index, 1);
    if (selectedElement.value?.id === targetId) {
      selectedElement.value = null;
    }
    return;
  }

  // If not found in main elements, search in nested groups
  const deleteFromGroup = (group: GroupElement): boolean => {
    const childIndex = group.children.findIndex((el) => el.id === targetId);
    if (childIndex !== -1) {
      group.children.splice(childIndex, 1);
      if (selectedElement.value?.id === targetId) {
        selectedElement.value = null;
      }
      return true;
    }

    // Search recursively in nested groups
    for (const child of group.children) {
      if (child.type === 'group') {
        if (deleteFromGroup(child as GroupElement)) {
          return true;
        }
      }
    }
    return false;
  };

  // Search in all top-level groups
  for (const element of layout.elements) {
    if (element.type === 'group') {
      if (deleteFromGroup(element as GroupElement)) {
        return;
      }
    }
  }
};

const updateElement = (updatedElement: LayoutElement) => {
  // First try to update in main elements
  const index = layout.elements.findIndex((el) => el.id === updatedElement.id);
  if (index !== -1) {
    layout.elements[index] = updatedElement;
    if (selectedElement.value?.id === updatedElement.id) {
      selectedElement.value = updatedElement;
    }
    return;
  }

  // Check if it's a child label within a BigLabelElement
  const updateChildLabel = (elements: LayoutElement[]): boolean => {
    for (const element of elements) {
      if (element.type === 'bigTextSmallLabel') {
        const bigLabel = element as BigLabelElement;
        if (bigLabel.children && bigLabel.children.id === updatedElement.id) {
          bigLabel.children = updatedElement as LabelElement;
          if (selectedElement.value?.id === updatedElement.id) {
            selectedElement.value = updatedElement;
          }
          return true;
        }
      } else if (element.type === 'group') {
        if (updateChildLabel((element as GroupElement).children)) {
          return true;
        }
      }
    }
    return false;
  };

  // Search for child labels in all elements
  if (updateChildLabel(layout.elements)) {
    return;
  }

  // If not found in main elements, search in nested groups
  const updateInGroup = (group: GroupElement): boolean => {
    const childIndex = group.children.findIndex(
      (el) => el.id === updatedElement.id
    );
    if (childIndex !== -1) {
      group.children[childIndex] = updatedElement;
      if (selectedElement.value?.id === updatedElement.id) {
        selectedElement.value = updatedElement;
      }
      return true;
    }

    // Search recursively in nested groups
    for (const child of group.children) {
      if (child.type === 'group') {
        if (updateInGroup(child as GroupElement)) {
          return true;
        }
      }
    }
    return false;
  };

  // Search in all top-level groups
  for (const element of layout.elements) {
    if (element.type === 'group') {
      if (updateInGroup(element as GroupElement)) {
        return;
      }
    }
  }
};

// Actions
const clearCanvas = () => {
  layout.elements = [];
  selectedElement.value = null;

  // Also remove from localStorage if deviceId is set
  if (layout.siteId) {
    const storageKey = `mockAHUData_site_${layout.siteId}`;
    const existingLayouts = JSON.parse(
      localStorage.getItem(storageKey) || '[]'
    );
    const updatedLayouts = existingLayouts.filter(
      (item: AHULayoutContract) => item.siteId !== layout.siteId
    );
    localStorage.setItem(storageKey, JSON.stringify(updatedLayouts));
    console.log('Layout removed from localStorage for siteId:', layout.siteId);
  }
};

const previewLayout = () => {
  showPreview.value = true;
};

const saveLayout = () => {
  // Get existing layouts from localStorage
  const existingLayouts = JSON.parse(
    localStorage.getItem('mockVAVData') || '[]'
  );

  // Find index of layout with same siteId (if it exists)
  const siteId = layout.siteId || 0; // Default to 0 if no siteId
  const existingIndex = existingLayouts.findIndex(
    (item: AHULayoutContract) => item.siteId === siteId
  );

  // Update existing layout or add new one
  if (existingIndex !== -1) {
    existingLayouts[existingIndex] = { ...layout, siteId };
  } else {
    existingLayouts.push({ ...layout, siteId });
  }

  // Save back to localStorage
  // localStorage.setItem('mockAHUData', JSON.stringify(existingLayouts));
  localStorage.setItem('mockVAVData', JSON.stringify(existingLayouts));

  console.log('Saving layout:', { ...layout, siteId });
  console.log('All saved layouts:', existingLayouts);

  // Show success message
  // You could add a toast notification here
  alert('Layout saved successfully!');
};

// Load any existing layout
const loadLayout = () => {
  // Try to load from site-specific storage first
  const siteId = layout.siteId || 0;
  const siteStorageKey = `mockAHUData_site_${siteId}`;
  const savedSiteLayouts = localStorage.getItem(siteStorageKey);

  if (savedSiteLayouts) {
    try {
      const layoutsArray = JSON.parse(savedSiteLayouts);
      // Load the first layout for this site if available
      if (layoutsArray.length > 0) {
        Object.assign(layout, layoutsArray[0]);
        return;
      }
    } catch (error) {
      console.error(
        'Failed to load saved layouts from site-specific storage:',
        error
      );
    }
  }

  // Try to load from old format for backward compatibility
  const savedLayouts = localStorage.getItem('mockVAVData');
  if (savedLayouts) {
    try {
      const layoutsArray = JSON.parse(savedLayouts);
      // For backward compatibility, load the first layout available
      if (layoutsArray.length > 0) {
        Object.assign(layout, layoutsArray[0]);
        return;
      }
    } catch (error) {
      console.error('Failed to load saved layouts from mockVAVData:', error);
    }
  }

  // Fallback to even older format for backward compatibility
  const oldSaved = localStorage.getItem('ahu-layout-generator');
  if (oldSaved) {
    try {
      const savedLayout = JSON.parse(oldSaved);
      Object.assign(layout, savedLayout);
    } catch (error) {
      console.error('Failed to load saved layout from old format:', error);
    }
  }
};

// Initialize
loadLayout();

// Global drag end handler to ensure state is always cleaned up
const handleGlobalDragEnd = () => {
  isDragging.value = false;
  dragOverIndex.value = -1;
  draggedElementType = null;
};

// Add global event listeners for drag end
if (typeof window !== 'undefined') {
  window.addEventListener('dragend', handleGlobalDragEnd);
  window.addEventListener('drop', handleGlobalDragEnd);
}

// Cleanup event listeners on component unmount
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('dragend', handleGlobalDragEnd);
    window.removeEventListener('drop', handleGlobalDragEnd);
  }
});
</script>

<style lang="scss" scoped>
.layout-generator {
  height: 100vh;
  padding: 0;
}

.palette-panel,
.properties-panel {
  height: calc(100vh - 64px);
}

.canvas-panel {
  height: calc(100vh - 64px);
}

.canvas-container {
  position: relative;
  width: 100%;
  min-height: 500px;
  max-height: calc(100vh - 180px);
  background: #fafafa;
  border: 2px dashed #ddd;
  overflow-y: auto;
  overflow-x: hidden;
  transition: all 0.3s ease;

  &.dragging-active {
    background: #f3f8ff;
    border-color: #1976d2;
    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.15);
  }

  &.show-grid {
    background-image: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px
      ),
      linear-gradient(rgba(0, 0, 0, 0.1) 1px, transparent 1px);
    background-size: calc(100% / 12) 20px;
  }

  .grid-guidelines {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;

    .grid-line {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 1px;
      background: rgba(255, 0, 0, 0.3);
    }
  }

  .layout-content {
    position: relative;
    z-index: 2;
    width: 100%;
    min-height: calc(100% - 32px);
    padding: 16px;
    padding-bottom: 32px; /* Extra bottom padding for better scrolling */

    // Bootstrap Grid System CSS
    .container-fluid {
      width: 100%;
      padding-right: 15px;
      padding-left: 15px;
      margin-right: auto;
      margin-left: auto;
    }

    .row {
      display: flex;
      flex-wrap: wrap;
      margin-right: -15px;
      margin-left: -15px;
    }

    // Bootstrap Column Classes
    [class*='col-'] {
      position: relative;
      width: 100%;
      padding-right: 15px;
      padding-left: 15px;
    }

    // Large screen columns (lg)
    @media (min-width: 992px) {
      .col-lg-1 {
        flex: 0 0 8.333333%;
        max-width: 8.333333%;
      }
      .col-lg-2 {
        flex: 0 0 16.666667%;
        max-width: 16.666667%;
      }
      .col-lg-3 {
        flex: 0 0 25%;
        max-width: 25%;
      }
      .col-lg-4 {
        flex: 0 0 33.333333%;
        max-width: 33.333333%;
      }
      .col-lg-5 {
        flex: 0 0 41.666667%;
        max-width: 41.666667%;
      }
      .col-lg-6 {
        flex: 0 0 50%;
        max-width: 50%;
      }
      .col-lg-7 {
        flex: 0 0 58.333333%;
        max-width: 58.333333%;
      }
      .col-lg-8 {
        flex: 0 0 66.666667%;
        max-width: 66.666667%;
      }
      .col-lg-9 {
        flex: 0 0 75%;
        max-width: 75%;
      }
      .col-lg-10 {
        flex: 0 0 83.333333%;
        max-width: 83.333333%;
      }
      .col-lg-11 {
        flex: 0 0 91.666667%;
        max-width: 91.666667%;
      }
      .col-lg-12 {
        flex: 0 0 100%;
        max-width: 100%;
      }

      // Large screen offsets
      .offset-lg-1 {
        margin-left: 8.333333%;
      }
      .offset-lg-2 {
        margin-left: 16.666667%;
      }
      .offset-lg-3 {
        margin-left: 25%;
      }
      .offset-lg-4 {
        margin-left: 33.333333%;
      }
      .offset-lg-5 {
        margin-left: 41.666667%;
      }
      .offset-lg-6 {
        margin-left: 50%;
      }
      .offset-lg-7 {
        margin-left: 58.333333%;
      }
      .offset-lg-8 {
        margin-left: 66.666667%;
      }
      .offset-lg-9 {
        margin-left: 75%;
      }
      .offset-lg-10 {
        margin-left: 83.333333%;
      }
      .offset-lg-11 {
        margin-left: 91.666667%;
      }
    }

    // Small screen columns (sm)
    @media (min-width: 576px) {
      .col-sm-1 {
        flex: 0 0 8.333333%;
        max-width: 8.333333%;
      }
      .col-sm-2 {
        flex: 0 0 16.666667%;
        max-width: 16.666667%;
      }
      .col-sm-3 {
        flex: 0 0 25%;
        max-width: 25%;
      }
      .col-sm-4 {
        flex: 0 0 33.333333%;
        max-width: 33.333333%;
      }
      .col-sm-5 {
        flex: 0 0 41.666667%;
        max-width: 41.666667%;
      }
      .col-sm-6 {
        flex: 0 0 50%;
        max-width: 50%;
      }
      .col-sm-7 {
        flex: 0 0 58.333333%;
        max-width: 58.333333%;
      }
      .col-sm-8 {
        flex: 0 0 66.666667%;
        max-width: 66.666667%;
      }
      .col-sm-9 {
        flex: 0 0 75%;
        max-width: 75%;
      }
      .col-sm-10 {
        flex: 0 0 83.333333%;
        max-width: 83.333333%;
      }
      .col-sm-11 {
        flex: 0 0 91.666667%;
        max-width: 91.666667%;
      }
      .col-sm-12 {
        flex: 0 0 100%;
        max-width: 100%;
      }

      // Small screen offsets
      .offset-sm-1 {
        margin-left: 8.333333%;
      }
      .offset-sm-2 {
        margin-left: 16.666667%;
      }
      .offset-sm-3 {
        margin-left: 25%;
      }
      .offset-sm-4 {
        margin-left: 33.333333%;
      }
      .offset-sm-5 {
        margin-left: 41.666667%;
      }
      .offset-sm-6 {
        margin-left: 50%;
      }
      .offset-sm-7 {
        margin-left: 58.333333%;
      }
      .offset-sm-8 {
        margin-left: 66.666667%;
      }
      .offset-sm-9 {
        margin-left: 75%;
      }
      .offset-sm-10 {
        margin-left: 83.333333%;
      }
      .offset-sm-11 {
        margin-left: 91.666667%;
      }
    }

    // Default mobile-first (below sm breakpoint)
    .col-1 {
      flex: 0 0 8.333333%;
      max-width: 8.333333%;
    }
    .col-2 {
      flex: 0 0 16.666667%;
      max-width: 16.666667%;
    }
    .col-3 {
      flex: 0 0 25%;
      max-width: 25%;
    }
    .col-4 {
      flex: 0 0 33.333333%;
      max-width: 33.333333%;
    }
    .col-5 {
      flex: 0 0 41.666667%;
      max-width: 41.666667%;
    }
    .col-6 {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .col-7 {
      flex: 0 0 58.333333%;
      max-width: 58.333333%;
    }
    .col-8 {
      flex: 0 0 66.666667%;
      max-width: 66.666667%;
    }
    .col-9 {
      flex: 0 0 75%;
      max-width: 75%;
    }
    .col-10 {
      flex: 0 0 83.333333%;
      max-width: 83.333333%;
    }
    .col-11 {
      flex: 0 0 91.666667%;
      max-width: 91.666667%;
    }
    .col-12 {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
}

.palette-section {
  .v-chip {
    display: flex;
    margin-bottom: 8px;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }
}

.preview-container {
  max-height: 400px;
  overflow: auto;
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
}

// Drop zone indicators
.drop-zone-indicator {
  position: relative;
  height: 12px;
  margin: 8px 0;
  z-index: 10;
  transition: all 0.2s ease;

  .drop-line {
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, #1976d2, #42a5f5);
    border-radius: 3px;
    box-shadow: 0 3px 12px rgba(25, 118, 210, 0.5);
    animation: pulse-drop-line 1.5s ease-in-out infinite;

    &::before {
      content: '';
      position: absolute;
      left: -8px;
      top: -2px;
      width: 0;
      height: 0;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      border-right: 8px solid #1976d2;
    }

    &::after {
      content: '';
      position: absolute;
      right: -8px;
      top: -2px;
      width: 0;
      height: 0;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      border-left: 8px solid #42a5f5;
    }
  }
}

@keyframes pulse-drop-line {
  0%,
  100% {
    opacity: 0.7;
    transform: scaleY(1);
  }
  50% {
    opacity: 1;
    transform: scaleY(1.2);
  }
}

// Dragging spacing improvements
.dragging-spacing {
  transition: all 0.2s ease;
  margin-top: 8px !important;
  margin-bottom: 8px !important;

  // Add subtle visual feedback during drag
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}
</style>
